/**
 * i18n render guard — defense-in-depth against raw message keys leaking to the UI.
 *
 * Even with CI catching new bare-key references, a stray i18n key (e.g. `menu.finance`)
 * must never render verbatim to an end user (Quality Gate Rule 1 — UPT-37 / UPT-173).
 * This guard detects bare dotted message keys and, as a last resort, humanizes the
 * final path segment ("menu.finance" -> "Finance") so users always see readable text.
 */

/** Matches a bare i18n message key: dotted, identifier-like segments, no whitespace. */
const BARE_KEY_RE = /^[A-Za-z][\w-]*(?:\.[A-Za-z][\w-]*)+$/;

/** True when the value looks like a bare i18n message key (e.g. `menu.finance`). */
export function isBareMessageKey(value: unknown): value is string {
  return typeof value === 'string' && BARE_KEY_RE.test(value.trim());
}

/** Humanize a key (or its last dotted segment) into Title Case words. */
export function humanizeMessageKey(value: string): string {
  const segment = value.trim().split('.').pop() ?? value;
  const words = segment
    // split camelCase / PascalCase boundaries: "fileLoading" -> "file Loading"
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    // split on separators: snake_case / kebab-case -> spaces
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!words) return value;
  return words
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

type ResolveMessage = (id: string) => string | undefined;

/**
 * Resolve a UI label, guaranteeing no bare i18n key ever renders.
 * - non-bare-key strings are returned unchanged (already human text)
 * - bare keys are resolved via `resolve` (typically `intl.formatMessage`)
 * - if resolution is missing or echoes the key back, the key is humanized as a last resort
 */
export function guardLabel(
  label: string | null | undefined,
  resolve?: ResolveMessage,
): string {
  if (label == null) return '';
  if (!isBareMessageKey(label)) return label;
  const key = label.trim();
  const resolved = resolve?.(key);
  if (resolved && resolved !== key && !isBareMessageKey(resolved)) {
    return resolved;
  }
  return humanizeMessageKey(key);
}
